import { useCallback, useSyncExternalStore } from "react";
import { Calendar, X } from "lucide-react";

const KEY = "tsi-following-showexpired";
const listeners = new Set();

// Reads the persisted choice. Default is "hidden" — past/expired events are not shown
// until the user turns them on.
function read() {
    try { return localStorage.getItem(KEY) === "true"; } catch { return false; }
}
function emit() { listeners.forEach((l) => l()); }

// Update the shared value: persist + notify every subscriber on the page.
export function setShowExpiredValue(next) {
    try { localStorage.setItem(KEY, String(next)); } catch { /* ignore */ }
    emit();
}

function subscribe(cb) {
    listeners.add(cb);
    const onStorage = (e) => { if (e.key === KEY) cb(); };
    window.addEventListener("storage", onStorage);
    return () => { listeners.delete(cb); window.removeEventListener("storage", onStorage); };
}

// Shared "show past/expired events" state — live-synced across the Following drawer
// and the Your Network page, and remembered between visits.
export function useShowExpired() {
    const showExpired = useSyncExternalStore(subscribe, read, () => false);
    const setShowExpired = useCallback((val) => {
        setShowExpiredValue(typeof val === "function" ? val(read()) : val);
    }, []);
    return [showExpired, setShowExpired];
}

// Green calendar with a red ✕ centered inside — marks a past / expired event.
export function CalX({ cal = "#22c55e", x = "#ef4444", size = 16 }) {
    return (
        <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
            <Calendar style={{ width: size, height: size, color: cal }} />
            <span className="absolute inset-0 flex items-center justify-center">
                <X style={{ width: size * 0.6, height: size * 0.6, color: x }} strokeWidth={3.2} />
            </span>
        </span>
    );
}

// The colored/grey toggle button used to show or hide past events.
export function ExpiredEventsToggle({ showExpired, onToggle, testid, size = 16 }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onToggle}
            title={showExpired ? "Expired events shown — tap to hide them (you'll still get their notifications)" : "Expired events hidden — tap to show them"}
            aria-label={showExpired ? "Hide expired events" : "Show expired events"}
            className="inline-flex items-center justify-center h-8 w-8 border border-[#2a2a2a] bg-[#0f0f0f] hover:border-[#3a3a3a] transition-colors"
        >
            {showExpired ? <CalX size={size} /> : <CalX size={size} cal="#666" x="#666" />}
        </button>
    );
}
